import { useEffect } from 'react'

import { engineFetch } from '@/features/mitra/api/askClient'
import {
  type RolloutStatus,
  isNotInRollout,
  onNotInRollout,
  useRolloutStore,
} from '@/features/mitra/api/rollout'
import { useAuthStore } from '@/lib/auth/store'
import { tokenClaims } from '@/lib/auth/token-expiry'
import { config } from '@/lib/config'

/**
 * The soft launch's gate: whether Mitra is open to the signed-in reader.
 *
 * `GET /v1/access`, as the reader (the same bearer every Mitra call carries,
 * `askClient.ts`), answers `{"enabled": true|false}`. Asked once after
 * sign-in and held for the page, then asked again about every ten minutes, so
 * a reader let in mid-session gets Mitra without a reload, and at once
 * (at most every thirty seconds) after any call is refused with
 * `not_in_rollout`. Every build asks, staging and dev included.
 *
 * A check that got no definite answer (offline, a timeout, a 404 or a 5xx
 * from the engine or the proxy in front of it, a 401 after the session was
 * exchanged again, a broken body) changes nothing a
 * reader already has: a blip mid-session never throws them out of Mitra.
 * Nor does it on a reload: the last definite answer is kept in this browser
 * for the reader it was about (`rememberAnswer`), so a reader the engine let
 * in keeps Mitra when the first check of a page load gets no answer (an
 * engine restarting, or taken out of its proxy while it reports not ready).
 * Only a first check with nothing known, now or before, reads it as shut.
 * Either way it is tried again sooner, backing off to the ten minutes. A
 * definite no (`enabled: false`, or a 403 `not_in_rollout`) shuts Mitra at
 * once and forgets the earlier yes.
 */

/** How long an answer is held before it is asked for again. */
export const ACCESS_RECHECK_MS = 10 * 60_000
/** The first retry after a check that failed; doubled on each failure after it. */
export const ACCESS_RETRY_MS = 30_000
/** The least time between two re-checks a refusal asks for: no storm of them. */
const REFUSAL_GAP_MS = 30_000
const ACCESS_TIMEOUT_MS = 10_000

let inFlight: Promise<void> | null = null
let timer: ReturnType<typeof setTimeout> | null = null
/** Bumped on sign-out, so an answer that lands after it is not kept. */
let generation = 0
let failures = 0
let lastRefusalCheck = Number.NEGATIVE_INFINITY

/** Asks the engine unless a fresh answer is held or a check is already out. */
export function ensureMitraAccess(): void {
  if (!config.mitraLaunchersEnabled || inFlight !== null) return
  if (Date.now() < useRolloutStore.getState().staleAt) return
  void checkMitraAccess()
}

/** Asks the engine now, sharing a check already in flight. */
export function checkMitraAccess(): Promise<void> {
  if (!config.mitraLaunchersEnabled) return Promise.resolve()
  if (inFlight !== null) return inFlight
  const mine = generation
  const run = readAccess().then(({ status, failed, reached }) => {
    if (mine !== generation) return
    failures = failed ? failures + 1 : 0
    const wait = failed
      ? Math.min(ACCESS_RETRY_MS * 2 ** (failures - 1), ACCESS_RECHECK_MS)
      : ACCESS_RECHECK_MS
    // No answer keeps the last one, this page load's or, on a first check,
    // the one this browser kept for the reader; only a reader with neither
    // is shut by it.
    const reader = readerKey()
    const known = useRolloutStore.getState().status
    let next: Exclude<RolloutStatus, 'pending'>
    if (!failed) {
      next = status
      rememberAnswer(reader, status)
    } else if (known !== 'pending') {
      next = known
    } else {
      next = rememberedYes(reader) ? 'enabled' : status
    }
    useRolloutStore.setState({ status: next, reachable: reached, staleAt: Date.now() + wait })
    schedule(wait)
  })
  const entry = run.finally(() => {
    if (inFlight === entry) inFlight = null
  })
  inFlight = entry
  return entry
}

/** Forgets the answer: a sign-out, or a test. Mitra stays shut until it is asked again. */
export function resetMitraAccess(): void {
  generation += 1
  inFlight = null
  failures = 0
  lastRefusalCheck = Number.NEGATIVE_INFINITY
  if (timer !== null) clearTimeout(timer)
  timer = null
  useRolloutStore.setState({ status: 'pending', reachable: true, staleAt: 0 })
}

async function readAccess(): Promise<{
  status: Exclude<RolloutStatus, 'pending'>
  failed: boolean
  /** Whether the engine itself answered (false: offline, a timeout, a 404, a 5xx). */
  reached: boolean
}> {
  const noAnswer = { status: 'disabled', failed: true } as const
  try {
    const response = await engineFetch('/v1/access', { timeoutMs: ACCESS_TIMEOUT_MS })
    // Every engine serves the route now, so a 404 is the proxy in front of
    // one it has dropped (an engine reporting not ready), as a 5xx is: no
    // answer about the rollout, and no sign the engine is there.
    if (response.status === 404 || response.status >= 500) return { ...noAnswer, reached: false }
    if (response.ok) {
      const body: unknown = await response.json().catch(() => null)
      const enabled =
        typeof body === 'object' && body !== null
          ? (body as Record<string, unknown>)['enabled']
          : undefined
      if (typeof enabled !== 'boolean') return { ...noAnswer, reached: true }
      return { status: enabled ? 'enabled' : 'disabled', failed: false, reached: true }
    }
    if (await isNotInRollout(response)) return { status: 'disabled', failed: false, reached: true }
    // A 401 (after `askClient.ts` exchanged the session again once): no
    // answer about the rollout, asked again soon.
    return { ...noAnswer, reached: true }
  } catch {
    // Offline, a timeout, a blocked origin: no answer, asked again soon.
    return { ...noAnswer, reached: false }
  }
}

/** Where this browser keeps the last reader the engine let in. */
const REMEMBERED_KEY = 'mm-mitra-access'

/**
 * The signed-in reader, as a short hash of their token's subject (mm-be's
 * `user_id`, else `sub`), or `null` when the token says nobody: then nothing
 * is kept or read. A hash, so the browser's storage never holds the id.
 */
function readerKey(): string | null {
  const token = useAuthStore.getState().accessToken
  if (token === null) return null
  const claims = tokenClaims(token)
  const subject = claims?.['user_id'] ?? claims?.['sub']
  if (typeof subject !== 'string' && typeof subject !== 'number') return null
  // FNV-1a, 32 bits: enough to tell two readers of one browser apart.
  let hash = 0x811c9dc5
  for (const char of String(subject)) {
    hash ^= char.codePointAt(0) ?? 0
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16)
}

/** Keeps a definite answer for `reader`: a yes is kept, a no forgets it. */
function rememberAnswer(reader: string | null, status: Exclude<RolloutStatus, 'pending'>): void {
  try {
    if (status === 'enabled' && reader !== null) localStorage.setItem(REMEMBERED_KEY, reader)
    else localStorage.removeItem(REMEMBERED_KEY)
  } catch {
    // Storage refused (a private window, blocked site data): nothing kept.
  }
}

/** Whether the engine last let `reader` in, as this browser kept it. */
function rememberedYes(reader: string | null): boolean {
  if (reader === null) return false
  try {
    return localStorage.getItem(REMEMBERED_KEY) === reader
  } catch {
    return false
  }
}

function schedule(wait: number): void {
  if (timer !== null) clearTimeout(timer)
  timer = setTimeout(() => {
    timer = null
    if (useAuthStore.getState().status === 'authenticated') void checkMitraAccess()
  }, wait)
}

// A call refused with `not_in_rollout` asks again, unless a check is already
// out or one was asked this way in the last thirty seconds.
onNotInRollout(() => {
  // A definite no: the yes this browser kept is no longer the reader's.
  rememberAnswer(null, 'disabled')
  if (inFlight !== null) return
  const now = Date.now()
  if (now - lastRefusalCheck < REFUSAL_GAP_MS) return
  lastRefusalCheck = now
  void checkMitraAccess()
})

// A sign-out forgets the answer: the next member is asked about afresh.
useAuthStore.subscribe((state, previous) => {
  if (previous.status === 'authenticated' && state.status === 'anonymous') resetMitraAccess()
})

/** Mitra's state for this reader: `off` when the build ships no engine at all. */
export type MitraAccess = RolloutStatus | 'off'

/**
 * Mitra's access for the reader, asking the engine once they are signed in.
 * Every launcher, entry point and suggestion reads this (or
 * `useMitraEnabled`), never `config.mitraLaunchersEnabled` alone.
 */
export function useMitraAccess(): MitraAccess {
  const status = useRolloutStore((state) => state.status)
  const signedIn = useAuthStore((state) => state.status === 'authenticated')
  useEffect(() => {
    if (signedIn) ensureMitraAccess()
  }, [signedIn])
  return config.mitraLaunchersEnabled ? status : 'off'
}

/** False while the last access check could not reach the engine. */
export function useMitraReachable(): boolean {
  return useRolloutStore((state) => state.reachable)
}

/** Whether to draw Mitra's launchers, entry points and suggestions. */
export function useMitraEnabled(): boolean {
  return useMitraAccess() === 'enabled'
}
